import { BaseLoggerConfig, LoggerConfig } from '../config';

/**
 * MinimalRuntimeConfig describes the minimal amount of configuration needed
 * during initialization of a logger.
 *
 * "Runtime" references this configuration
 * is a pre-resolved/validated configuration being loaded directly by something
 * such as an env (i.e., `process.env`).
 */
type MinimalRuntimeConfig = Pick<BaseLoggerConfig, 'environment' | 'service'>;

/**
 * RuntimeConfig is a helper type to allow Typescript to assume a specific runtime
 * configuration, `T`, extends `MinimalRuntimeConfig`.
 *
 * @see MinimalRuntimeConfig
 */
type RuntimeConfig<T extends MinimalRuntimeConfig> = T;

/**
 * Runtime defines the runtime environments any Logger can support.
 */
enum Runtime {
  FALLBACK = 'fallback',
  DEV = 'dev',
  TEST = 'test',
  SERVERLESS = 'serverless',
  BROWSER = 'browser',
}

/**
 * RuntimeLoggerConfigProvider describes a function capable of taking a
 * `RuntimeConfiguration` and returning a `LoggerConfig`.
 *
 * This is useful for taking env values and parsing them into a usable configuration
 * object or value.
 */
interface RuntimeLoggerConfigProvider<T extends MinimalRuntimeConfig, U extends BaseLoggerConfig> {
  (conf: RuntimeConfig<T>): LoggerConfig<U>;
}

/**
 * getDefaultMetaFromRuntimeConfig extracts common meta data from a `RuntimeConfig` and
 * the provided `Runtime`.
 */
const getDefaultMetaFromRuntimeConfig = <T extends MinimalRuntimeConfig>(
  configType: Runtime,
  { environment, service }: RuntimeConfig<T>
) => {
  return {
    environment,
    service,
    config: configType,
  };
};

/**
 * RuntimeConfigs describe an object providing a `RuntimeLoggerConfigProvider`
 * for each Runtime.
 */
interface RuntimeConfigs<T extends MinimalRuntimeConfig, U extends BaseLoggerConfig> {
  fallback: RuntimeLoggerConfigProvider<T, U>;
  dev: RuntimeLoggerConfigProvider<T, U>;
  test: RuntimeLoggerConfigProvider<T, U>;
  serverless: RuntimeLoggerConfigProvider<T, U>;
  browser: RuntimeLoggerConfigProvider<T, U>;
}

/**
 * RuntimeConfigValidator is a function that returns an error message or null
 * for a given RuntimeConfig partial.
 */
type RuntimeConfigValidator<T extends MinimalRuntimeConfig> = (conf: Partial<T>) => string | null;

/**
 * RuntimeConfigValidators is an array of (configurationKey, `RuntimeConfigValidator`) tuples.
 *
 * The array of tuples represents the needed validation for a given `RuntimeConfig`. Tuples
 * are used to simplify types as maintaining keys is difficult and requires casting since
 * `Object` helpers don't all support generics.
 */
type RuntimeConfigValidators<T extends MinimalRuntimeConfig> = [keyof T, RuntimeConfigValidator<T>[]][];

/**
 * validateRuntimeConfig returns whether a given `RuntimeConfig` is valid for a set
 * of `RuntimeConfigValidators`.
 */
const validateRuntimeConfig = <T extends MinimalRuntimeConfig>(
  maybeConf: Partial<T>,
  validators: RuntimeConfigValidators<T>
): { isValid: boolean; validationErrors: Record<keyof T, string | null> } => {
  let isValid = true;
  const validationErrors = Object.fromEntries(validators.map((k) => [k[0], null])) as Record<keyof T, string | null>;

  // Iterate through every validator tuple
  validators.forEach((validatorTuple) => {
    // Iterate through every validation fn until the first error
    const [key, validatorFns] = validatorTuple;
    validatorFns.every((fn) => {
      const validationMessage = fn(maybeConf);
      if (validationMessage != null) {
        validationErrors[key] = validationMessage;
        isValid = false;
        return false;
      }

      return true;
    });
  });

  return {
    isValid,
    validationErrors,
  };
};

export type {
  MinimalRuntimeConfig,
  RuntimeConfig,
  RuntimeConfigs,
  RuntimeLoggerConfigProvider,
  RuntimeConfigValidator,
  RuntimeConfigValidators,
};
export { Runtime, getDefaultMetaFromRuntimeConfig, validateRuntimeConfig };
